import { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import type { Session, User } from '@supabase/supabase-js';
import { supabase } from '@lib/supabase';
import { installDeepLinkAuthHandler, isNative, signInWithGoogleNative } from '@lib/capacitorAuth';

interface AuthContextValue {
  session: Session | null;
  user: User | null;
  loading: boolean;
  signInWithPassword: (email: string, password: string) => Promise<{ error: string | null }>;
  signInWithOtp: (email: string) => Promise<{ error: string | null }>;
  signInWithGoogle: () => Promise<{ error: string | null }>;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    supabase.auth.getSession().then(({ data }) => {
      setSession(data.session);
      setLoading(false);
    });

    const { data: sub } = supabase.auth.onAuthStateChange((_event, s) => {
      setSession(s);
    });

    // Capacitor native only: listen for deep-link OAuth callbacks from
    // the system browser (Google auth → info.gasweb.streamflow://auth-callback).
    // No-op on the web; harmless to call always.
    const teardownDeepLink = installDeepLinkAuthHandler();

    return () => {
      sub.subscription.unsubscribe();
      teardownDeepLink();
    };
  }, []);

  const signInWithPassword = async (email: string, password: string) => {
    const { error } = await supabase.auth.signInWithPassword({ email, password });
    return { error: error?.message ?? null };
  };

  const signInWithOtp = async (email: string) => {
    const { error } = await supabase.auth.signInWithOtp({
      email,
      options: { emailRedirectTo: window.location.origin },
    });
    return { error: error?.message ?? null };
  };

  /**
   * Google OAuth — same flow for sign-in AND sign-up. New users get a
   * Supabase auth row created on first callback; returning users just
   * get a fresh session. The redirect lands back at the dashboard,
   * which is what the rest of the app expects.
   *
   * `prompt: 'select_account'` forces Google to show the account picker
   * even when the user has only one Google account signed in — handy
   * when a household member is on a shared Chrome profile and wants to
   * pick a different identity.
   *
   * On Capacitor native we delegate to signInWithGoogleNative() which
   * opens the system browser and waits for a deep-link callback — the
   * web flow's window.location.origin redirect doesn't work because
   * the redirect would open in a browser, not back into the app.
   */
  const signInWithGoogle = async () => {
    if (isNative()) {
      return signInWithGoogleNative();
    }
    const { error } = await supabase.auth.signInWithOAuth({
      provider: 'google',
      options: {
        redirectTo: window.location.origin,
        queryParams: { prompt: 'select_account' },
      },
    });
    return { error: error?.message ?? null };
  };

  const signOut = async () => {
    await supabase.auth.signOut();
  };

  return (
    <AuthContext.Provider
      value={{
        session,
        user: session?.user ?? null,
        loading,
        signInWithPassword,
        signInWithOtp,
        signInWithGoogle,
        signOut,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
